import React, { useState, useEffect, lazy, Suspense } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";

import AdminProtectedRoute from "./components/admin/AdminProtectedRoute";
import Layout from "./components/Layout";
import ScrollToTopButton from "./components/ScrollToTopButton";
import ScrollToTop from "./components/ScrollToTop";
import { FloatingButtonsProvider } from "./context/FloatingButtonsContext";

// 🔥 Lazy load heavy global components
const Navbar = lazy(() => import("./components/Navbar"));
const WhatsAppButton = lazy(() => import("./components/WhatsAppButton"));
const QuickTaxiButton = lazy(() => import("./components/QuickTaxiButton"));

// User Pages
const Home = lazy(() => import("./pages/Home"));
const About = lazy(() => import("./pages/About"));
const CommunityImpact = lazy(() => import("./pages/CommunityImpact"));
const Contact = lazy(() => import("./pages/Contact"));
const Destination = lazy(() => import("./pages/Destination"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogDetail = lazy(() => import("./pages/BlogDetail"));
const Experiences = lazy(() => import("./pages/Experiences"));
const ExperienceDetail = lazy(() => import("./pages/ExperienceDetail"));
const DayTour = lazy(() => import("./pages/DayTour"));
const RoundTour = lazy(() => import("./pages/RoundTour"));
const TourDetail = lazy(() => import("./pages/TourDetail"));
const RoundTourDetail = lazy(() => import("./pages/RoundTourDetail"));
const TailorMadeTours = lazy(() => import("./pages/TailorMadeTours"));
const Events = lazy(() => import("./pages/Events"));
const EventDetail = lazy(() => import("./pages/EventDetail"));
const BookEventTour = lazy(() => import("./components/BookEventTour"));
const QuickTaxi = lazy(() => import("./pages/QuickTaxi"));
const TermsConditions = lazy(() => import("./pages/TermsConditions"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));

// Common Pages (Admin & Super Admin)
const AdminLogin = lazy(() => import("./pages/admin/Login"));
const ForgotPassword = lazy(() => import("./pages/admin/ForgotPassword"));
const ChangePassword = lazy(() => import("./pages/admin/ChangePassword"));
const AdminManageAbout = lazy(() => import("./pages/admin/ManageAbout"));
const AdminManageCommunityImpact = lazy(() =>
  import("./pages/admin/ManageCommunityImpact")
);
const ManageDestination = lazy(() => import("./pages/admin/ManageDestination"));
const DestinationList = lazy(() =>
  import("./pages/destinations/DestinationList")
);
const AddDestination = lazy(() =>
  import("./pages/destinations/AddDestination")
);
const EditDestination = lazy(() =>
  import("./pages/destinations/EditDestination")
);

const BlogList = lazy(() => import("./pages/blog/BlogList"));
const AddBlog = lazy(() => import("./pages/blog/AddBlog"));
const EditBlog = lazy(() => import("./pages/blog/EditBlog"));
const BlogView = lazy(() => import("./pages/blog/BlogView"));
const AdminTailorMade = lazy(() =>
  import("./pages/admin/ManageTailorMadeTour")
);
const DayTourList = lazy(() => import("./pages/daytour/DayTourList"));
const AddDayTour = lazy(() => import("./pages/daytour/AddDayTour"));
const EditDayTour = lazy(() => import("./pages/daytour/EditDayTour"));
const RoundTourList = lazy(() => import("./pages/roundtour/RoundTourList"));
const AddRoundTour = lazy(() => import("./pages/roundtour/AddRoundTour"));
const EditRoundTour = lazy(() => import("./pages/roundtour/EditRoundTour"));
const ExperienceList = lazy(() => import("./pages/experience/ExperienceList"));
const AddExperience = lazy(() => import("./pages/experience/AddExperience"));
const EditExperience = lazy(() => import("./pages/experience/EditExperience"));
const ExperienceView = lazy(() => import("./pages/experience/ExperienceView"));
const ContactList = lazy(() => import("./pages/contact/ContactList"));
const EditContact = lazy(() => import("./pages/contact/EditContact"));
const AdminManageHome = lazy(() => import("./pages/admin/ManageHome"));
const BlogComments = lazy(() => import("./pages/admin/BlogComments"));
const TourReviews = lazy(() => import("./pages/admin/TourReviews"));
const TailorComments = lazy(() => import("./pages/admin/TailorComments"));
const EventList = lazy(() => import("./pages/admin/EventList"));
const AddEvent = lazy(() => import("./pages/admin/AddEvent"));
const EditEvent = lazy(() => import("./pages/admin/EditEvent"));
const QuickTaxiBookingAdmin = lazy(() =>
  import("./pages/admin/QuickTaxiBooking")
);
const TaxiList = lazy(() => import("./pages/quickTaxi/TaxiList"));
const AddTaxi = lazy(() => import("./pages/quickTaxi/AddTaxi"));
const EditTaxi = lazy(() => import("./pages/quickTaxi/EditTaxi"));

const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminNotifications = lazy(() =>
  import("./pages/admin/AdminNotifications")
);

// Super Admin
const SuperAdminLayout = lazy(() =>
  import("./components/layouts/SuperAdminLayout")
);
const SuperAdminDashboard = lazy(() =>
  import("./pages/admin/SuperAdminDashboard")
);
const SectionNotifications = lazy(() =>
  import("./pages/admin/SectionNotifications")
);
const AdminSectionRequest = lazy(() =>
  import("./pages/admin/AdminSectionRequest")
);
const DayTourBookingAdmin = lazy(() => import("./pages/admin/DayTourBooking"));
const RoundTourBookingAdmin = lazy(() =>
  import("./pages/admin/RoundTourBooking")
);
const CustomizeTourBookingAdmin = lazy(() =>
  import("./pages/admin/CustomizeTourBooking")
);
const EventTourBookingAdmin = lazy(() =>
  import("./pages/admin/EventTourBookingAdmin")
);
const AddAdmin = lazy(() => import("./pages/admin/AddAdmin"));
const AdminList = lazy(() => import("./pages/admin/AdminList"));
const ManageTripadvisorReviews = lazy(() =>
  import("./pages/admin/ManageTripadvisorReviews")
);

const AdminLayout = lazy(() => import("./components/layouts/AdminLayout"));

function App() {
  const location = useLocation();
  const [showSessionModal, setShowSessionModal] = useState(false);

  const hideLayout =
    location.pathname.startsWith("/admin") ||
    location.pathname.startsWith("/super-admin");

  useEffect(() => {
    const handleSessionExpired = () => {
      setShowSessionModal(true);
      toast.error("Session expired. Please log in again.");
      sessionStorage.removeItem("adminToken");
      sessionStorage.removeItem("superadminToken");
    };

    window.addEventListener("sessionExpired", handleSessionExpired);
    return () =>
      window.removeEventListener("sessionExpired", handleSessionExpired);
  }, []);

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="flex flex-col min-h-screen">
        {!hideLayout && (
          <Suspense fallback={null}>
            <Navbar />
          </Suspense>
        )}

        <FloatingButtonsProvider>
          <ScrollToTop />

          {!hideLayout && (
            <Suspense fallback={null}>
              <WhatsAppButton />
              <QuickTaxiButton />
            </Suspense>
          )}

          <ScrollToTopButton />

          <main className="flex-grow flex flex-col justify-start">
            <Suspense fallback={null}>
              <Routes>
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/refund-policy" element={<RefundPolicy />} />
                <Route
                  path="/terms-and-conditions"
                  element={<TermsConditions />}
                />

                {/* USER ROUTES */}
                <Route
                  path="/"
                  element={
                    <Layout>
                      <Home />
                    </Layout>
                  }
                />
                <Route path="/about" element={<About />} />
                <Route
                  path="/community-impact"
                  element={
                    <Layout>
                      <CommunityImpact />
                    </Layout>
                  }
                />
                <Route
                  path="/contact"
                  element={
                    <Layout>
                      <Contact />
                    </Layout>
                  }
                />
                <Route
                  path="/destinations"
                  element={
                    <Layout>
                      <Destination />
                    </Layout>
                  }
                />
                <Route
                  path="/blog"
                  element={
                    <Layout>
                      <Blog />
                    </Layout>
                  }
                />
                <Route path="/blog/:slug" element={<BlogDetail />} />
                <Route path="/experience" element={<Experiences />} />
                <Route
                  path="/experience/:slug"
                  element={<ExperienceDetail />}
                />
                <Route
                  path="/day-tours"
                  element={
                    <Layout>
                      <DayTour />
                    </Layout>
                  }
                />
                <Route
                  path="/round-tours"
                  element={
                    <Layout>
                      <RoundTour />
                    </Layout>
                  }
                />
                <Route path="/day-tour-detail/:slug" element={<TourDetail />} />
                <Route
                  path="/round-tours/:slug"
                  element={<RoundTourDetail />}
                />
                <Route
                  path="/tailor-made-tours"
                  element={<TailorMadeTours />}
                />
                <Route
                  path="/events"
                  element={
                    <Layout>
                      <Events />
                    </Layout>
                  }
                />
                <Route path="/events/:slug" element={<EventDetail />} />
                <Route
                  path="/book-event"
                  element={
                    <Layout>
                      <BookEventTour />
                    </Layout>
                  }
                />
                <Route
                  path="/quick-taxi"
                  element={
                    <Layout>
                      <QuickTaxi />
                    </Layout>
                  }
                />

                {/* ---------------------------ADMIN, SUPER ADMIN LOGIN--------------------------- */}
                {/* ADMIN LOGIN */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route
                  path="/admin/forgot-password"
                  element={<ForgotPassword />}
                />
                <Route
                  path="/admin/change-password"
                  element={<ChangePassword />}
                />
                <Route
                  path="/super-admin/change-password"
                  element={<ChangePassword />}
                />

                {/* ---------------------------SUPER ADMIN ROUTES--------------------------- */}
                <Route
                  path="/super-admin"
                  element={
                    <AdminProtectedRoute>
                      <SuperAdminLayout />
                    </AdminProtectedRoute>
                  }
                >
                  {/* Dashboard */}
                  <Route path="dashboard" element={<SuperAdminDashboard />} />

                  {/* Section Requests & Notifications */}
                  <Route
                    path="section-request"
                    element={<AdminSectionRequest />}
                  />
                  <Route
                    path="section-notifications"
                    element={<SectionNotifications />}
                  />

                  {/* Our Story */}
                  <Route path="manage-about" element={<AdminManageAbout />} />
                  <Route
                    path="manage-community"
                    element={<AdminManageCommunityImpact />}
                  />

                  {/* Destinations */}
                  <Route
                    path="manage-destination"
                    element={<ManageDestination />}
                  />
                  <Route path="destinations" element={<DestinationList />} />
                  <Route path="destinations/new" element={<AddDestination />} />
                  <Route
                    path="destinations/edit/:id"
                    element={<EditDestination />}
                  />

                  {/* Experiences */}
                  <Route path="experiences" element={<ExperienceList />} />
                  <Route path="experiences/new" element={<AddExperience />} />
                  <Route
                    path="experiences/edit/:id"
                    element={<EditExperience />}
                  />
                  <Route
                    path="experiences/view/:id"
                    element={<ExperienceView />}
                  />

                  {/* Blogs */}
                  <Route path="blogs" element={<BlogList />} />
                  <Route path="blogs/new" element={<AddBlog />} />
                  <Route path="blogs/edit/:id" element={<EditBlog />} />
                  <Route path="blogs/view/:id" element={<BlogView />} />

                  {/* Tailor Made Tours */}
                  <Route
                    path="tailor-made-tours"
                    element={<AdminTailorMade />}
                  />

                  {/* Day Tours */}
                  <Route path="day-tours" element={<DayTourList />} />
                  <Route path="day-tours/new" element={<AddDayTour />} />
                  <Route path="day-tours/edit/:id" element={<EditDayTour />} />

                  {/* Events */}
                  <Route path="events" element={<EventList />} />
                  <Route path="events/new" element={<AddEvent />} />
                  <Route path="events/edit/:id" element={<EditEvent />} />

                  {/* Round Tours */}
                  <Route path="round-tours" element={<RoundTourList />} />
                  <Route path="round-tours/new" element={<AddRoundTour />} />
                  <Route
                    path="round-tours/edit/:id"
                    element={<EditRoundTour />}
                  />

                  {/* Contacts */}
                  <Route path="contacts" element={<ContactList />} />
                  <Route path="contacts/edit" element={<EditContact />} />

                  {/* Home */}
                  <Route path="manage-home" element={<AdminManageHome />} />

                  {/* Comments & Reviews */}
                  <Route path="blog-comments" element={<BlogComments />} />
                  <Route path="tour-reviews" element={<TourReviews />} />
                  <Route path="tailor-comments" element={<TailorComments />} />
                  <Route
                    path="tripadvisor-reviews"
                    element={<ManageTripadvisorReviews />}
                  />

                  {/* Bookings */}
                  <Route
                    path="day-tour-booking"
                    element={<DayTourBookingAdmin />}
                  />
                  <Route
                    path="event-tour-booking"
                    element={<EventTourBookingAdmin />}
                  />
                  <Route
                    path="round-tour-booking"
                    element={<RoundTourBookingAdmin />}
                  />
                  <Route
                    path="customize-tour"
                    element={<CustomizeTourBookingAdmin />}
                  />
                  <Route
                    path="quick-taxi-booking"
                    element={<QuickTaxiBookingAdmin />}
                  />

                  {/* Quick Taxi */}
                  <Route path="taxis" element={<TaxiList />} />
                  <Route path="taxis/new" element={<AddTaxi />} />
                  <Route path="taxis/edit/:id" element={<EditTaxi />} />

                  {/* Admin Management */}
                  <Route path="add-admin" element={<AddAdmin />} />
                  <Route path="admins" element={<AdminList />} />
                </Route>

                {/* ---------------------------ADMIN ROUTES--------------------------- */}
                <Route
                  path="/admin"
                  element={
                    <AdminProtectedRoute>
                      <AdminLayout />
                    </AdminProtectedRoute>
                  }
                >
                  {/* Dashboard */}
                  <Route path="dashboard" element={<AdminDashboard />} />

                  {/* Our Story */}
                  <Route path="manage-about" element={<AdminManageAbout />} />
                  <Route
                    path="manage-community"
                    element={<AdminManageCommunityImpact />}
                  />

                  {/* Destinations */}
                  <Route
                    path="manage-destination"
                    element={<ManageDestination />}
                  />
                  <Route path="destinations" element={<DestinationList />} />
                  <Route path="destinations/new" element={<AddDestination />} />
                  <Route
                    path="destinations/edit/:id"
                    element={<EditDestination />}
                  />

                  {/* Experiences */}
                  <Route path="experiences" element={<ExperienceList />} />
                  <Route path="experiences/new" element={<AddExperience />} />
                  <Route
                    path="experiences/edit/:id"
                    element={<EditExperience />}
                  />
                  <Route
                    path="experiences/view/:id"
                    element={<ExperienceView />}
                  />

                  {/* Blogs */}
                  <Route path="blogs" element={<BlogList />} />
                  <Route path="blogs/new" element={<AddBlog />} />
                  <Route path="blogs/edit/:id" element={<EditBlog />} />
                  <Route path="blogs/view/:id" element={<BlogView />} />

                  {/* Tailor Made Tours */}
                  <Route
                    path="tailor-made-tours"
                    element={<AdminTailorMade />}
                  />

                  {/* Day Tours */}
                  <Route path="day-tours" element={<DayTourList />} />
                  <Route path="day-tours/new" element={<AddDayTour />} />
                  <Route path="day-tours/edit/:id" element={<EditDayTour />} />

                  {/* Events */}
                  <Route path="events" element={<EventList />} />
                  <Route path="events/new" element={<AddEvent />} />
                  <Route path="events/edit/:id" element={<EditEvent />} />

                  {/* Round Tours */}
                  <Route path="round-tours" element={<RoundTourList />} />
                  <Route path="round-tours/new" element={<AddRoundTour />} />
                  <Route
                    path="round-tours/edit/:id"
                    element={<EditRoundTour />}
                  />

                  {/* Comments & Reviews */}
                  <Route path="blog-comments" element={<BlogComments />} />
                  <Route path="tour-reviews" element={<TourReviews />} />
                  <Route path="tailor-comments" element={<TailorComments />} />

                  {/* Quick Taxi */}
                  <Route
                    path="quick-taxi-booking"
                    element={<QuickTaxiBookingAdmin />}
                  />
                  <Route path="taxis" element={<TaxiList />} />
                  <Route path="taxis/new" element={<AddTaxi />} />
                  <Route path="taxis/edit/:id" element={<EditTaxi />} />

                  {/* Notifications */}
                  <Route
                    path="notifications"
                    element={<AdminNotifications />}
                  />
                </Route>
              </Routes>
            </Suspense>
          </main>
        </FloatingButtonsProvider>
      </div>

      {/* Session Expired Modal */}
      {showSessionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-xl w-80 text-center shadow-lg">
            <h2 className="text-lg font-bold mb-4">Session Expired</h2>
            <p className="mb-6">
              Your session has expired. Please log in again.
            </p>
            <button
              onClick={() => (window.location.href = "/admin/login")}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
            >
              Go to Login
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default App;
